import { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, Chip, CircularProgress, FormControl, FormControlLabel, InputLabel, MenuItem, Paper, Select, Stack, Switch, Typography } from '@mui/material';
import ContactPageIcon from '@mui/icons-material/ContactPage';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import { supabase } from '../config/supabase';

type Team = { id: string; name: string; shortName?: string | null };
type Member = {
  id: string;
  playerId: string | null;
  name: string;
  role: 'Captain' | 'Co-Captain' | null;
  isSelf: boolean;
  email?: string;
  phone?: string;
};
type Roster = {
  teamId: string;
  teamName: string;
  members: Member[];
  preferences: { shareEmail: boolean; sharePhone: boolean; email: string | null; phone: string | null };
};
type Props = {
  teams: Team[];
  authHeader: () => Promise<Record<string, string>>;
  notify: (severity: 'success' | 'error', message: string) => void;
  openAccount: () => void;
};

function phoneHref(value: string) {
  const trimmed = value.trim();
  return `tel:${trimmed.startsWith('+') ? '+' : ''}${trimmed.replace(/\D/g, '')}`;
}

const contactSharingSwitchSx = {
  '& .MuiSwitch-thumb': { backgroundColor: '#fff' },
  '& .MuiSwitch-track': { backgroundColor: '#DC2626', opacity: 1 },
  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#16A34A', opacity: 1 },
  '& .MuiSwitch-switchBase.Mui-disabled + .MuiSwitch-track': { opacity: 1 },
  '& .MuiSwitch-switchBase.Mui-disabled': { color: '#fff' },
};

export function PlayerPortalRosters({ teams, authHeader, notify, openAccount }: Props) {
  const [teamId, setTeamId] = useState(() => teams[0]?.id || '');
  const [sortBy, setSortBy] = useState<'first' | 'last'>('last');
  const [roster, setRoster] = useState<Roster | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!teams.some((team) => team.id === teamId)) setTeamId(teams[0]?.id || '');
  }, [teamId, teams]);

  const authorizedFetch = useCallback(async (url: string, init: RequestInit = {}) => {
    let response = await fetch(url, { ...init, headers: { ...(await authHeader()), ...init.headers } });
    if (response.status === 401) {
      const { data: refreshed, error: refreshError } = await supabase.auth.refreshSession();
      if (!refreshError && refreshed.session?.access_token) {
        response = await fetch(url, { ...init, headers: { Authorization: `Bearer ${refreshed.session.access_token}`, ...init.headers } });
      }
    }
    return response;
  }, [authHeader]);

  useEffect(() => {
    if (!teamId) return;
    const controller = new AbortController();
    setRoster(null);
    setLoading(true);
    setError(null);
    void (async () => {
      try {
        const response = await authorizedFetch(`/api/player/roster?teamId=${encodeURIComponent(teamId)}&sortBy=${sortBy}`, { signal: controller.signal });
        const result = await response.json() as Roster & { error?: string };
        if (!response.ok) throw new Error(result.error || 'Unable to load team roster.');
        if (!controller.signal.aborted) setRoster(result);
      } catch (cause) {
        if (!controller.signal.aborted) setError(cause instanceof Error ? cause.message : 'Unable to load team roster.');
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    })();
    return () => controller.abort();
  }, [authorizedFetch, sortBy, teamId]);

  const savePreference = async (field: 'shareEmail' | 'sharePhone', checked: boolean) => {
    if (!roster || saving) return;
    setSaving(true);
    try {
      const response = await authorizedFetch('/api/player/roster', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ teamId, sortBy, shareEmail: field === 'shareEmail' ? checked : roster.preferences.shareEmail, sharePhone: field === 'sharePhone' ? checked : roster.preferences.sharePhone }),
      });
      const result = await response.json() as Roster & { error?: string };
      if (!response.ok) throw new Error(result.error || 'Unable to save contact sharing.');
      if (result.teamId === teamId) setRoster(result);
      notify('success', 'Contact sharing updated for this team.');
    } catch (cause) {
      notify('error', cause instanceof Error ? cause.message : 'Unable to save contact sharing.');
    } finally {
      setSaving(false);
    }
  };

  const downloadContact = async (member: Member) => {
    if (!member.playerId || (!member.email && !member.phone)) return;
    setDownloadingId(member.id);
    try {
      const response = await authorizedFetch(`/api/player/roster/vcard?teamId=${encodeURIComponent(teamId)}&playerId=${encodeURIComponent(member.playerId)}`);
      if (!response.ok) {
        const result = await response.json().catch(() => ({})) as { error?: string };
        throw new Error(result.error || 'Unable to download contact.');
      }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const filename = response.headers.get('Content-Disposition')?.match(/filename="([^"\r\n]+)"/)?.[1] || 'contact.vcf';
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (cause) {
      notify('error', cause instanceof Error ? cause.message : 'Unable to download contact.');
    } finally {
      setDownloadingId(null);
    }
  };

  const leaders = roster?.members.filter((member) => member.role) || [];
  const players = roster?.members.filter((member) => !member.role) || [];
  const selectedTeam = teams.find((team) => team.id === teamId);
  const selectedTeamName = selectedTeam?.shortName || selectedTeam?.name || 'this team';
  const renderMember = (member: Member) => (
    <Box key={member.id} sx={{ py: 1.5, borderBottom: '1px solid', borderColor: 'divider', '&:last-child': { borderBottom: 0 } }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }}>
        <Box sx={{ minWidth: 0, width: '100%' }}>
          <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography variant="body1" sx={{ fontWeight: 800, overflowWrap: 'anywhere' }}>{member.name}</Typography>
            {member.role && <Chip size="small" label={member.role} />}
            {member.isSelf && <Chip size="small" variant="outlined" label="You" />}
          </Stack>
          <Stack spacing={0.25} sx={{ mt: member.email || member.phone ? 0.5 : 0 }}>
            {member.email && <Button component="a" href={`mailto:${member.email}`} size="small" startIcon={<EmailIcon />} sx={{ p: 0, minWidth: 0, alignSelf: 'flex-start', textTransform: 'none', textAlign: 'left', overflowWrap: 'anywhere' }}>{member.email}</Button>}
            {member.phone && <Button component="a" href={phoneHref(member.phone)} size="small" startIcon={<PhoneIcon />} sx={{ p: 0, minWidth: 0, alignSelf: 'flex-start', textTransform: 'none' }}>{member.phone}</Button>}
          </Stack>
        </Box>
        {(member.email || member.phone) && member.playerId && (
          <Button size="small" variant="outlined" startIcon={<ContactPageIcon />} onClick={() => void downloadContact(member)} disabled={downloadingId === member.id} sx={{ whiteSpace: 'nowrap', flexShrink: 0 }} aria-label={`Add ${member.name} to contacts`}>
            Add to Contacts
          </Button>
        )}
      </Stack>
    </Box>
  );

  return <Stack spacing={2}>
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 900 }}>Rosters</Typography>
      <Typography color="text.secondary">Your teams and contact details teammates chose to share.</Typography>
    </Box>
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
      {teams.length > 1 && <FormControl size="small" fullWidth><InputLabel>Team</InputLabel><Select label="Team" value={teamId} onChange={(event) => setTeamId(event.target.value)}>{teams.map((team) => <MenuItem key={team.id} value={team.id}>{team.shortName || team.name}</MenuItem>)}</Select></FormControl>}
      <FormControl size="small" fullWidth><InputLabel>Sort by</InputLabel><Select label="Sort by" value={sortBy} onChange={(event) => setSortBy(event.target.value as 'first' | 'last')}><MenuItem value="last">Last Name</MenuItem><MenuItem value="first">First Name</MenuItem></Select></FormControl>
    </Stack>
    {loading && <Stack direction="row" spacing={1} alignItems="center"><CircularProgress size={20} /><Typography variant="body2">Loading roster…</Typography></Stack>}
    {error && <Alert severity="error">{error}</Alert>}
    {roster?.teamId === teamId && <>
      <Paper variant="outlined" sx={{ p: 1.5 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>My Contact Sharing</Typography>
        <Typography variant="body2" color="text.secondary">Choose what teammates on {selectedTeamName} can see. Off by default.</Typography>
        <Stack sx={{ mt: 0.5 }}>
          <FormControlLabel control={<Switch sx={contactSharingSwitchSx} checked={roster.preferences.shareEmail} disabled={saving || !roster.preferences.email} onChange={(event) => void savePreference('shareEmail', event.target.checked)} />} label={`Share my email with this team${roster.preferences.email ? ` — ${roster.preferences.email}` : ''}`} />
          {!roster.preferences.email && <Button size="small" onClick={openAccount} sx={{ alignSelf: 'flex-start' }}>Add email in Account</Button>}
          <FormControlLabel control={<Switch sx={contactSharingSwitchSx} checked={roster.preferences.sharePhone} disabled={saving || !roster.preferences.phone} onChange={(event) => void savePreference('sharePhone', event.target.checked)} />} label={`Share my phone number with this team${roster.preferences.phone ? ` — ${roster.preferences.phone}` : ''}`} />
          {!roster.preferences.phone && <Typography variant="caption" color="text.secondary">No mobile phone is on your player profile. Ask your captain to add it.</Typography>}
        </Stack>
      </Paper>
      <Paper variant="outlined" sx={{ px: 1.5, pb: 0.5 }}>
        {leaders.length > 0 && <Box><Typography variant="subtitle2" sx={{ fontWeight: 900, pt: 1.5 }}>Captains</Typography>{leaders.map(renderMember)}</Box>}
        <Box><Typography variant="subtitle2" sx={{ fontWeight: 900, pt: 1.5 }}>Players</Typography>{players.length ? players.map(renderMember) : <Typography variant="body2" color="text.secondary" sx={{ py: 1.5 }}>No other players on this roster.</Typography>}</Box>
      </Paper>
    </>}
  </Stack>;
}
